import React from 'react';

import PropTypes from 'prop-types';
import { CSSProp } from 'styled-components';

import { useForwardedRef } from '@adsk/alloy-react-helpers';
import { stylePropType } from '@adsk/alloy-react-theme';

import EditButtonGroup from './EditButtonGroup';
import { InputProps } from './types';
import useInlineInput from './useInlineInput';

// role was intoduced for v17 and breaks projects using v16 types
// https://github.com/DefinitelyTyped/DefinitelyTyped/pull/53361
type InlineTextInputProps = Omit<InputProps<string>, 'onChange' | 'role'> & {
  saveUnmodifiedValue?: boolean;
  cancelOnBlur?: boolean;
};

/**
 * A TextInput for the InlineEdit component.
 */
const InlineTextInput = React.forwardRef<
  HTMLInputElement,
  InlineTextInputProps
>(
  (
    {
      placeholder,
      value,
      error,
      id,
      saveUnmodifiedValue = false,
      cancelOnBlur = false,
      ...props
    },
    ref,
  ) => {
    const inputRef = useForwardedRef(ref);
    const {
      getInputProps,
      getWrapperProps,
      handleCancel,
      handleSubmit,
      modified,
    } = useInlineInput<HTMLInputElement, string | undefined>({
      inputRef,
      value,
      saveUnmodifiedValue,
      cancelOnBlur,
      error,
    });

    const wrapperProps = getWrapperProps(props);

    return (
      <div {...wrapperProps}>
        <input
          {...getInputProps({
            placeholder,
            id,
            style: wrapperProps.style.input as CSSProp | undefined,
          })}
        />
        <EditButtonGroup
          handleCancel={handleCancel}
          handleSubmit={handleSubmit}
          modified={modified}
          error={error}
        />
      </div>
    );
  },
);

InlineTextInput.displayName = 'InlineTextInput';

InlineTextInput.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Toggle error state */
  error: PropTypes.bool,
  /** Placeholder text value */
  placeholder: PropTypes.string,
  /** Input id */
  id: PropTypes.string,
  /** The value of the field */
  value: PropTypes.string,
  /** If true the initial value can be saved without being modified, onChange will be fired when saving.
   * By default the user must modify the value before it can be saved */
  saveUnmodifiedValue: PropTypes.bool,
  /** If true the initial value will be restored when modified input is blurred */
  cancelOnBlur: PropTypes.bool,
};

export default InlineTextInput;
